import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import communityService, { GameLineupPlayerItem } from '@/services/communityService';

interface SubstitutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameSlug: string;
  playerOut: GameLineupPlayerItem;
  teamSide: 'home' | 'away';
  bench: GameLineupPlayerItem[];
  onSuccess: () => void;
}

const SubstitutionModal = ({
  isOpen,
  onClose,
  gameSlug,
  playerOut,
  teamSide,
  bench,
  onSuccess,
}: SubstitutionModalProps) => {
  const [playerInId, setPlayerInId] = useState<number | null>(null);
  const [minute, setMinute] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPlayerInId(null);
      setMinute('');
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSave =
    playerInId != null &&
    minute !== '' &&
    /^\d+$/.test(minute) &&
    parseInt(minute, 10) >= 0 &&
    parseInt(minute, 10) <= 120;

  const handleSubmit = async () => {
    if (!canSave || saving || playerInId == null) return;
    setError(null);
    setSaving(true);
    try {
      await communityService.postSubstitution(gameSlug, {
        time: `${minute}'`,
        team_side: teamSide,
        player_id_out: playerOut.player_participant_id,
        player_id_in: playerInId,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || (err instanceof Error ? err.message : 'Gagal melakukan pergantian'));
    } finally {
      setSaving(false);
    }
  };

  const playerOutLabel = [
    playerOut.player_number != null ? `#${playerOut.player_number}` : '',
    playerOut.name,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator */}
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

        {/* Header Title & Close Button */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-[#0E1726]">Substitusi Pemain</h2>
            <p className="text-xs font-semibold text-gray-400 mt-1">
              Ganti <span className="text-[#0E1726] font-bold">{playerOutLabel || playerOut.name}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3.5 py-2.5 rounded-2xl">
            {error}
          </div>
        )}

        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
          {/* Menit ke */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wider">
              Menit ke-
            </label>
            <input
              type="number"
              min={0}
              max={120}
              step={1}
              value={minute}
              onChange={(e) => setMinute(e.target.value.replace(/\D/g, '').slice(0, 3))}
              placeholder="Contoh: 65"
              className="w-full h-11 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold outline-none focus:border-[#FC480D] transition-colors"
            />
            <p className="text-[11px] text-gray-400 mt-1">Masukkan menit terjadinya pergantian (0–120)</p>
          </div>

          {/* Pilih Pemain Masuk */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
              Pilih pemain masuk (Bench)
            </label>
            <div className="space-y-2">
              {bench.map((p) => {
                const id = p.player_participant_id;
                const isSelected = playerInId === id;
                const label = [
                  p.player_number != null ? `#${p.player_number}` : '',
                  p.name,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlayerInId(id)}
                    className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-[#FC480D] bg-orange-50/50 shadow-xs'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                        <Icon icon="ph:arrows-left-right-bold" className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${isSelected ? 'text-[#0E1726]' : 'text-gray-700'}`}>
                          {label || p.name}
                        </p>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          BENCH
                        </span>
                      </div>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        isSelected
                          ? 'border-[#FC480D] bg-[#FC480D] text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Icon icon="ph:check-bold" className="w-3 h-3" />}
                    </div>
                  </button>
                );
              })}
              {bench.length === 0 && (
                <p className="text-xs text-gray-400 italic py-3 text-center">
                  Tidak ada pemain di bangku cadangan
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-11 px-4 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave || saving}
            className={`h-11 px-4 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-sm font-bold shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 ${
              !canSave || saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
            }`}
          >
            {saving ? (
              <>
                <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <span>Proses Substitusi</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubstitutionModal;
